import { FC, useContext, useMemo } from "react";

import { Stack } from "@mui/material";
import { FormType, Locales } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { LocaleContext } from "../../../../../../common/contexts/LocaleContext";
import { FormDialog } from "../../../../../forms-design-system/FormDialog/FormDialog";
import { useFormV2Context } from "../../../contexts/FormV2Context";

import { CartTicketRow } from "./CartTicketRow/CartTicketRow";
import {
  buildCartRows,
  decrementPurchasedNumberInForm,
  formatPrice,
  removeNthParticipantAnswerFor,
  syncPwycAmountForVariantAfterDecrement,
} from "./helper";
import { ShopCartLineItem, useShopCartContext } from "./ShopCartContext";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput, TicketPurchase } from "@/common/types/ticketing";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Shop } from "@/icons/outlined";

const EMPTY_LINE_ITEMS: ShopCartLineItem[] = [];
const EMPTY_PARTICIPANTS: NonNullable<TicketingPaymentInput["customAnswersOfParticipants"]> = [];
const EMPTY_TICKETS: TicketPurchase[] = [];

interface ShopCartDrawerProps {
  open: boolean;
  onClose: () => void;
  onCheckout: () => void;
  canCheckout: boolean;
}

export const ShopCartDrawer: FC<ShopCartDrawerProps> = ({ open, onClose, onCheckout, canCheckout }) => {
  const { t } = useTranslate();
  const { locale } = useContext(LocaleContext);
  const { questions, validRatesSorted, form, formCurrency } = useFormV2Context(FormType.Ticketing);
  const { isSmallScreen } = useMediaQuery();
  const shopCart = useShopCartContext();
  const lineItems = shopCart?.lineItems ?? EMPTY_LINE_ITEMS;

  // Watching participants so the cart re-renders when the user submits answers through the
  // variant flow (handleShopAddToCart appends to customAnswersOfParticipants).
  const participants = useWatch({ control: form.control, name: "customAnswersOfParticipants" }) ?? EMPTY_PARTICIPANTS;
  // Watching ticketsPurchased so PWYC unit prices update live as the user types in the input.
  const ticketsPurchased = useWatch({ control: form.control, name: "ticketsPurchased" }) ?? EMPTY_TICKETS;

  const cartRows = useMemo(
    () => buildCartRows({ lineItems, validRatesSorted, questions, participants, ticketsPurchased, locale }),
    [lineItems, validRatesSorted, questions, participants, ticketsPurchased, locale]
  );

  const totalItems = lineItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotalAmount = useMemo(() => cartRows.reduce((sum, row) => sum + row.unitPrice, 0), [cartRows]);

  // e.g. handleDeleteTicket("tshirt-S-Red", 1) → removes the 2nd "S Red" ticket (and its
  // associated participant answers), leaving the 1st and 3rd entries intact.
  const handleDeleteTicket = (rateId: string, ticketIndexWithinRate: number) => {
    const decrement = decrementPurchasedNumberInForm({ form, rateId });
    if (decrement) {
      syncPwycAmountForVariantAfterDecrement({ form, validRatesSorted, ...decrement });
    }
    removeNthParticipantAnswerFor({ form, rateId, ticketIndexWithinRate });
    shopCart?.decrementItem(rateId);
  };

  const handleCheckout = () => {
    onClose();
    onCheckout();
  };

  const isEmpty = cartRows.length === 0;

  return (
    <FormDialog
      variant={isSmallScreen ? "drawer" : "default"}
      size="xs"
      open={open}
      onClose={onClose}
      icon={<Shop />}
      title={t("ticketing", "shop.yourCart")}
      actions={[
        <FormButton
          key="checkout"
          variant="solid"
          size="large"
          fullWidth
          onClick={handleCheckout}
          disabled={!canCheckout}
          data-test="shop-cart-checkout-button"
          sx={{ boxShadow: "0px 16px 32px 0px rgba(0, 0, 0, 0.12)" }}
        >
          {t("ticketing", "shop.checkout", { count: totalItems })}
        </FormButton>,
      ]}
    >
      {isEmpty ? (
        <Stack>
          <FormTypography variant="body2" vibe="moderate">
            {t("ticketing", "shop.cartEmpty")}
          </FormTypography>
          <FormTypography variant="body2" vibe="moderate">
            {t("ticketing", "shop.cartEmptyHint")}
          </FormTypography>
        </Stack>
      ) : (
        <Stack gap={2}>
          <Stack gap={1.5}>
            {cartRows.map((row) => (
              <CartTicketRow
                key={row.key}
                row={row}
                formCurrency={formCurrency}
                locale={locale as Locales}
                removeLabel={t("ticketing", "shop.removeFromCart")}
                onDelete={() => handleDeleteTicket(row.rateId, row.ticketIndexWithinRate)}
              />
            ))}
          </Stack>
          <FormDivider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <FormTypography variant="subtitle2" vibe="intense">
              {t("ticketing", "shop.total")}
            </FormTypography>
            <FormTypography variant="body2" vibe="intense" data-test="shop-cart-total-amount">
              {formatPrice(cartTotalAmount, formCurrency, locale as Locales)}
            </FormTypography>
          </Stack>
        </Stack>
      )}
    </FormDialog>
  );
};
